'use client';

import { useCallback, useMemo } from 'react';
import {
  WebPageFeaturesTab,
  type FeatureRelatedRequestActions,
} from '@terra/ui-design-system';
import type { FeatureRelatedRequestDto } from '@terra/shared-contracts-web-pages';
import { useHttpReplay } from '../../hooks/useHttpReplay';
import { useLayoutStore, useMainContentTabs } from '../../store/layoutStore';

type WebPageFeaturesContentProps = {
  webpageId: string;
  webpageScreenshotUrl?: string | null;
};

const relatedRequestPath = (url: string): string => {
  try {
    return new URL(url).pathname || url;
  } catch {
    return url;
  }
};

export function WebPageFeaturesContent({
  webpageId,
  webpageScreenshotUrl,
}: WebPageFeaturesContentProps) {
  const { navigateInActiveTab } = useMainContentTabs();
  const { openHarRequestInReplay } = useHttpReplay();
  const setEndpointTab = useLayoutStore((state) => state.setEndpointTab);
  const setSelectedRequestsContentId = useLayoutStore(
    (state) => state.setSelectedRequestsContentId,
  );

  const onOpenEndpoint = useCallback(
    (request: FeatureRelatedRequestDto) => {
      if (!request.endpointId) {
        return;
      }

      const path = relatedRequestPath(request.url);

      navigateInActiveTab({
        id: `endpoint-${request.endpointId}`,
        title: `${request.method.toUpperCase()} ${path}`,
        type: 'endpoint',
        data: { endpointId: request.endpointId },
      });
      setEndpointTab(request.endpointId, 'requests');
      setSelectedRequestsContentId(request.id);
    },
    [navigateInActiveTab, setEndpointTab, setSelectedRequestsContentId],
  );

  const onOpenWebpage = useCallback(
    (request: FeatureRelatedRequestDto) => {
      if (!request.webpageId) {
        return;
      }

      navigateInActiveTab({
        id: `webpage-${request.webpageId}`,
        title: relatedRequestPath(request.url),
        type: 'webpage',
        data: { id: request.webpageId },
      });
    },
    [navigateInActiveTab],
  );

  const onReplay = useCallback(
    (request: FeatureRelatedRequestDto) => {
      openHarRequestInReplay({
        method: request.method,
        url: request.url,
        headers: { request: request.requestHeaders },
        rawHeaders: {},
        requestBody: request.requestBody ?? undefined,
        httpVersion: request.httpVersion ?? undefined,
      });
    },
    [openHarRequestInReplay],
  );

  const relatedRequestActions = useMemo<FeatureRelatedRequestActions>(
    () => ({ onOpenEndpoint, onOpenWebpage, onReplay }),
    [onOpenEndpoint, onOpenWebpage, onReplay],
  );

  return (
    <WebPageFeaturesTab
      webpageId={webpageId}
      webpageScreenshotUrl={webpageScreenshotUrl}
      relatedRequestActions={relatedRequestActions}
    />
  );
}
